import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Save, LayoutTemplate, PanelTop, PanelBottom, Sparkles, MousePointerClick, Instagram, Twitter } from 'lucide-react';
import { toast as toastify } from 'react-toastify';
import R2Upload from '../R2Upload';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Fixed list of social platforms (cannot be added/removed/renamed)
const SOCIAL_PLATFORMS = [
  { key: 'instagram', name: 'Instagram' },
  { key: 'onlyfans', name: 'OnlyFans' },
  { key: 'reddit', name: 'Reddit' },
  { key: 'twitter', name: 'Twitter' }
];

// Build footerLinks ensuring one entry per platform, preserving saved data
const normalizeFooterLinks = (existing = []) => {
  const byKey = new Map(existing.map(l => [l.icon, l]));
  return SOCIAL_PLATFORMS.map(p => {
    const saved = byKey.get(p.key) || {};
    return {
      name: p.name,
      icon: p.key,
      url: saved.url || '',
      iconUrl: saved.iconUrl || ''
    };
  });
};

// Reusable section wrapper with icon, title, description
const Section = ({ icon: Icon, title, description, children }) => (
  <section className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
    <header className="flex items-start gap-3 px-5 md:px-6 py-4 border-b border-gray-100 bg-gray-50/50">
      <div className="shrink-0 w-10 h-10 rounded-lg bg-sky-50 text-sky-600 flex items-center justify-center">
        <Icon className="w-5 h-5" />
      </div>
      <div className="flex-1">
        <h3 className="font-semibold text-gray-900">{title}</h3>
        {description && <p className="text-sm text-gray-500 mt-0.5">{description}</p>}
      </div>
    </header>
    <div className="px-5 md:px-6 py-5 space-y-5">{children}</div>
  </section>
);

const SettingsForm = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settingsFormData, setSettingsFormData] = useState({
    headerLogo: '',
    footerLogo: '',
    buttonIcon: '',
    ctaButtonLink: '',
    introHeadline: '',
    introText: '',
    footerLinks: []
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings`);
      const data = response.data;
      setSettingsFormData({
        headerLogo: data.headerLogo || '',
        footerLogo: data.footerLogo || '',
        buttonIcon: data.buttonIcon || '',
        ctaButtonLink: data.ctaButtonLink || '',
        introHeadline: data.introHeadline || '',
        introText: data.introText || '',
        footerLinks: normalizeFooterLinks(data.footerLinks)
      });
      setLoading(false);
    } catch (error) {
      console.error('Error fetching settings:', error);
      setLoading(false);
    }
  };

  const handleSettingsInputChange = (e) => {
    const { name, value } = e.target;
    setSettingsFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleFooterLinkChange = (index, field, value) => {
    const newLinks = [...settingsFormData.footerLinks];
    newLinks[index] = { ...newLinks[index], [field]: value };
    setSettingsFormData(prev => ({ ...prev, footerLinks: newLinks }));
  };

  const handleSettingsUpdate = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/settings`, settingsFormData, { withCredentials: true });
      toastify.success('Settings saved successfully!', {
        position: "top-right",
        autoClose: 3000,
      });
      fetchSettings();
    } catch (error) {
      toastify.error('Failed to update settings', {
        position: "top-right",
        autoClose: 3000,
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-sky-500"></div>
      </div>
    );
  }

  const socialIcon = (key) => {
    if (key === 'instagram') return Instagram;
    if (key === 'twitter') return Twitter;
    return Sparkles;
  };

  return (
    <div className="max-w-5xl mx-auto">
      {/* Page header */}
      <div className="flex items-center justify-between mb-6 md:mb-8">
        <div>
          <h2 className="text-xl md:text-2xl font-bold text-gray-900">Site Settings</h2>
          <p className="text-sm text-gray-500 mt-1">Manage the content, branding, and social links of your landing page.</p>
        </div>
        <Button
          onClick={handleSettingsUpdate}
          disabled={saving}
          className="hidden md:flex items-center gap-2"
          data-testid="settings-save-btn-top"
        >
          {saving ? (
            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
          ) : (
            <Save className="w-4 h-4" />
          )}
          {saving ? 'Saving...' : 'Save Settings'}
        </Button>
      </div>

      <div className="space-y-6">
        {/* 1. Hero / Intro */}
        <Section
          icon={LayoutTemplate}
          title="Hero Intro"
          description="The headline and paragraph shown at the top of the landing page."
        >
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Intro Headline</label>
            <Input
              name="introHeadline"
              value={settingsFormData.introHeadline}
              onChange={handleSettingsInputChange}
              placeholder="ACCESS ALL OUR PREVIOUSLY RELEASED EXCLUSIVE VIDEOS! 🔓"
              data-testid="settings-intro-headline"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Intro Text</label>
            <Textarea
              name="introText"
              value={settingsFormData.introText}
              onChange={handleSettingsInputChange}
              rows={4}
              placeholder="We are a young couple that loves to have sex..."
              data-testid="settings-intro-text"
            />
          </div>
        </Section>

        {/* 2. Header */}
        <Section
          icon={PanelTop}
          title="Header"
          description="Logo shown in the top navigation bar."
        >
          <R2Upload
            label="Header Logo (replaces 'Leolulu VIDEO STORE' text)"
            name="headerLogo"
            value={settingsFormData.headerLogo}
            onChange={handleSettingsInputChange}
            acceptedFormats="images"
            folderPath="logos"
            placeholder="Enter URL or click Upload"
          />
        </Section>

        {/* 3. CTA Button (used on each video card) */}
        <Section
          icon={MousePointerClick}
          title="Video Card CTA"
          description="The 'GET IT NOW ON' button that appears on every video card."
        >
          <R2Upload
            label="Button Icon"
            name="buttonIcon"
            value={settingsFormData.buttonIcon}
            onChange={handleSettingsInputChange}
            acceptedFormats="images"
            folderPath="logos"
            placeholder="Enter URL or click Upload"
          />
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Button Destination URL</label>
            <Input
              name="ctaButtonLink"
              value={settingsFormData.ctaButtonLink}
              onChange={handleSettingsInputChange}
              placeholder="https://onlyfans.com/my/chats/chat/960542/"
              className="w-full"
              data-testid="settings-cta-link"
            />
            <p className="text-xs text-gray-500 mt-1.5">Opens when users click the "GET IT NOW ON" button.</p>
          </div>
        </Section>

        {/* 4. Footer */}
        <Section
          icon={PanelBottom}
          title="Footer"
          description="Logo and social links shown at the bottom of the page."
        >
          <R2Upload
            label="Footer Logo (replaces 'LEOLULU' text)"
            name="footerLogo"
            value={settingsFormData.footerLogo}
            onChange={handleSettingsInputChange}
            acceptedFormats="images"
            folderPath="logos"
            placeholder="Enter URL or click Upload"
          />

          {/* Social Links */}
          <div>
            <div className="text-sm font-medium text-gray-700 mb-3">Social Links</div>
            <div className="space-y-3">
              {settingsFormData.footerLinks.map((link, index) => {
                const SIcon = socialIcon(link.icon);
                return (
                  <div
                    key={link.icon || index}
                    className="rounded-lg border border-gray-200 hover:border-gray-300 transition-colors"
                  >
                    <div className="flex items-center gap-3 px-4 py-3 border-b border-gray-100 bg-gray-50/60">
                      <div className="w-8 h-8 rounded-md bg-white border border-gray-200 flex items-center justify-center text-gray-700">
                        <SIcon className="w-4 h-4" />
                      </div>
                      <div className="font-semibold text-sm text-gray-900 w-20">{link.name}</div>
                      <Input
                        placeholder={`https://… ${link.name.toLowerCase()} profile URL`}
                        value={link.url}
                        onChange={(e) => handleFooterLinkChange(index, 'url', e.target.value)}
                        className="flex-1"
                        data-testid={`social-url-${link.icon}`}
                      />
                    </div>
                    <div className="px-4 py-3">
                      <R2Upload
                        label="Custom icon (optional — overrides default)"
                        name={`footerLinks[${index}].iconUrl`}
                        value={link.iconUrl || ''}
                        onChange={(e) => handleFooterLinkChange(index, 'iconUrl', e.target.value)}
                        acceptedFormats="images"
                        folderPath="logos"
                        placeholder="Enter URL or click Upload"
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </Section>
      </div>

      {/* Sticky-ish save bar on mobile (always visible) */}
      <div className="mt-8 flex justify-end">
        <Button
          onClick={handleSettingsUpdate}
          disabled={saving}
          size="lg"
          className="flex items-center gap-2"
          data-testid="settings-save-btn-bottom"
        >
          {saving ? (
            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
          ) : (
            <Save className="w-4 h-4" />
          )}
          {saving ? 'Saving...' : 'Save Settings'}
        </Button>
      </div>
    </div>
  );
};

export default SettingsForm;
